import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  Icon,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import LanguageSwitcher from "components/LanguageSwitcher.js";
import { t } from "i18next";
import SoftButton from "components/SoftButton";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } =
    controller;
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  const manager = localStorage.getItem("manager");
  let token = localStorage.getItem("token");

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("warehouseName");
    localStorage.removeItem("manager");
    localStorage.removeItem("selectedWarehouse");
    navigate("/authentication/connexion");
  };

  const handleLogoutModalOpen = () => {
    setLogoutModalOpen(true);
    setProfileMenuAnchor(null);
  };
  const handleLogoutModalClose = () => setLogoutModalOpen(false);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => {
    setOpenConfigurator(dispatch, !openConfigurator);
    setProfileMenuAnchor(null);
  };

  const handleProfileMenuOpen = (event) =>
    setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);

  /* 
    Notifications are currently handled automatically on app load in App.js
    Manual notification controls removed as per cleanup request.
  */


  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
          <Icon
            fontSize="medium"
            sx={{ ...navbarDesktopMenu, ml: -5 }}
            onClick={handleMiniSidenav}
          >
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </SoftBox>

        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox display="flex" alignItems="center" gap={2}>
              <LanguageSwitcher />

              {token ? (
                <SoftBox display="flex" alignItems="center" gap={1}>
                  {/* Profile Menu Trigger */}
                  <SoftBox
                    onClick={handleProfileMenuOpen}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      background: "rgba(255, 255, 255, 0.9)",
                      borderRadius: "12px",
                      padding: "8px 16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 1)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "#2563eb",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        mr: 1.5,
                      }}
                    >
                      {manager ? manager.charAt(0).toUpperCase() : "M"}
                    </Avatar>

                    <SoftBox sx={{ display: { xs: "none", sm: "block" } }}>
                      <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        sx={{
                          color: "#2d3748",
                          fontSize: "0.875rem",
                          lineHeight: 1,
                        }}
                      >
                        {manager || "Manager"}
                      </SoftTypography>
                    </SoftBox>

                    <Icon sx={{ ml: 1, fontSize: "1.2rem", color: "#a0aec0" }}>
                      keyboard_arrow_down
                    </Icon>
                  </SoftBox>

                  {/* Profile Menu */}
                  <Menu
                    anchorEl={profileMenuAnchor}
                    open={Boolean(profileMenuAnchor)}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        mt: 1.5,
                        minWidth: 280,
                        borderRadius: "12px",
                        background: "white",
                        border: "1px solid rgba(0,0,0,0.08)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        overflow: "visible",
                        "&:before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 20,
                          width: 10,
                          height: 10,
                          bgcolor: "white",
                          transform: "translateY(-50%) rotate(45deg)",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderBottom: "none",
                          borderRight: "none",
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    {/* Profile Header */}
                    <MenuItem
                      sx={{
                        py: 1,
                        px: 1,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      <SoftBox display="flex" alignItems="center" width="100%">
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "#2563eb",
                            mr: 2,
                            fontSize: "1.1rem",
                            fontWeight: "600",
                          }}
                        >
                          {manager ? manager.charAt(0).toUpperCase() : "M"}
                        </Avatar>
                        <SoftBox>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            color="#2d3748"
                          >
                            {manager || "Manager"}
                          </Typography>
                        </SoftBox>
                      </SoftBox>
                    </MenuItem>

                    <Divider sx={{ mx: 1 }} />

                    {/* Notifications Section */}
                    <MenuItem
                      sx={{
                        px: 1,
                        "&:hover": { backgroundColor: "#f7fafc" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon sx={{ color: "#4a5568" }}>notifications</Icon>
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          variant="h6"
                          fontWeight="500"
                          color="#2d3748"
                        >
                          Notifications
                        </Typography>
                      </ListItemText>
                    </MenuItem>

                    {/* Recent Notifications Placeholder - Backend integration needed if history is required */}
                    <SoftBox sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text">
                        {t("Aucune nouvelle notification")}
                      </Typography>
                    </SoftBox>

                    <Divider sx={{ mx: 1 }} />

                    {/* Settings */}
                    <MenuItem
                      onClick={handleConfiguratorOpen}
                      sx={{
                        px: 1,
                        "&:hover": { backgroundColor: "#f7fafc" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon sx={{ color: "#4a5568" }}>settings</Icon>
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          variant="h6"
                          fontWeight="500"
                          color="#2d3748"
                        >
                          {t("Paramètres")}
                        </Typography>
                      </ListItemText>
                    </MenuItem>

                    <Divider sx={{ mx: 1 }} />

                    {/* Logout */}
                    <MenuItem
                      onClick={handleLogoutModalOpen}
                      sx={{
                        py: 1.5,
                        px: 1,
                        "&:hover": { backgroundColor: "#fef5f5" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon sx={{ color: "#e53e3e" }}>logout</Icon>
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          variant="h6"
                          fontWeight="500"
                          color="#e53e3e"
                        >
                          {t("Déconnexion")}
                        </Typography>
                      </ListItemText>
                    </MenuItem>
                  </Menu>

                  {/* Logout Confirmation Modal */}
                  <Dialog
                    open={logoutModalOpen}
                    onClose={handleLogoutModalClose}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                      sx: {
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    <DialogTitle>
                      <SoftTypography
                        variant="h6"
                        fontWeight="600"
                        color="#2d3748"
                      >
                        {t("Confirmer la déconnexion")}
                      </SoftTypography>
                    </DialogTitle>
                    <DialogContent>
                      <SoftTypography variant="caption" color="#4a5568">
                        {t("Êtes-vous sûr de vouloir vous déconnecter ?")}
                      </SoftTypography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                      <SoftButton
                        onClick={handleLogoutModalClose}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderRadius: "8px" }}
                      >
                        {t("Annuler")}
                      </SoftButton>
                      <SoftButton
                        onClick={handleLogout}
                        variant="gradient"
                        color="error"
                        sx={{ borderRadius: "8px" }}
                      >
                        {t("Se déconnecter")}
                      </SoftButton>
                    </DialogActions>
                  </Dialog>
                </SoftBox>
              ) : (
                <Link
                  to="/authentication/connexion"
                  style={{ textDecoration: "none" }}
                >
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      background: "#2563eb",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      color: "white",
                      textDecoration: "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: "#5a67d8",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <SoftTypography
                      variant="button"
                      fontWeight="medium"
                      color="white"
                    >
                      Se connecter
                    </SoftTypography>
                  </SoftBox>
                </Link>
              )}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
